import { FC, useCallback, useEffect, useReducer, useState } from 'react';
import CoverImage from 'src/components/common/CoverImage';
import useCurrentSubpage from 'src/store/appState/hooks/useCurrentSubpage';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGSkeleton from 'src/styles/theme/components/NGSkeleton';
import { ESize, ESubmenuType } from 'src/styles/theme/utils/enums';
import SliderDots from '../SliderDots';
import SliderInformation from '../SliderInformation';
import { StyledImageContainer, StyledInformationContainer, StyledSlider } from './Slider.styles';
import { ISlider, TIndex, TIndexReducer } from './Slider.type';

let sliderInterval: NodeJS.Timeout;

const Slider: FC<ISlider> = ({ news }) => {
	const [{ type: currentSubpageType }] = useCurrentSubpage();

	const [isNextOnClickActivated, setIsNextOnClickActivated] = useState(true);

	const areNewsReady = news.length > 0;

	const startAutoPlay = useCallback(() => {
		clearInterval(sliderInterval);

		sliderInterval = setInterval(() => {
			nextSlide(undefined);
		}, 5000);
	}, []);

	const [selectedIndex, nextSlide] = useReducer<TIndexReducer>((state: TIndex, action: TIndex) => {
		if (typeof action === 'number' && action >= 0) {
			startAutoPlay();
			return action;
		}

		if (state === undefined) {
			return 1;
		}

		if (state + 1 < 3) {
			return state + 1;
		}

		return 0;
	}, 0);

	const stopAutoPlay = useCallback(() => {
		clearInterval(sliderInterval);
	}, []);

	useEffect(() => {
		if (currentSubpageType === ESubmenuType.devhubOverview) {
			startAutoPlay();
		} else {
			stopAutoPlay();
		}
	}, [currentSubpageType, startAutoPlay, stopAutoPlay]);

	useEffect(() => {
		if (areNewsReady) {
			startAutoPlay();
		}
	}, [areNewsReady, startAutoPlay]);

	useEffect(() => {
		return () => {
			stopAutoPlay();
		};
	}, [startAutoPlay, stopAutoPlay]);

	return (
		<StyledSlider
			onMouseEnter={() => stopAutoPlay()}
			onMouseLeave={() => startAutoPlay()}
			padding={ESize['2xs']}
			mdPaddingLeft={ESize.unset}
			onClick={() => {
				if (isNextOnClickActivated) nextSlide(undefined);
			}}
		>
			{areNewsReady ? (
				<StyledImageContainer>
					{news.map((item, index) => (
						<CoverImage key={item.id} isVisible={index === selectedIndex} src={item.coverSrc} />
					))}
				</StyledImageContainer>
			) : (
				<StyledImageContainer>
					<NGSkeleton width={'100%'} height={'100%'} transparentBackground />
				</StyledImageContainer>
			)}

			<Flex fullHeight horizontal={EFlex.between} vertical={EFlex.end}>
				{areNewsReady ? (
					<StyledInformationContainer
						onMouseEnter={() => setIsNextOnClickActivated(false)}
						onMouseLeave={() => setIsNextOnClickActivated(true)}
					>
						{news.map((item, index) => (
							<SliderInformation key={item.id} isVisible={index === selectedIndex} {...item} />
						))}
					</StyledInformationContainer>
				) : (
					<StyledInformationContainer>
						<NGSkeleton width={400} height={94} withBorder />
					</StyledInformationContainer>
				)}

				{areNewsReady ? (
					<SliderDots
						onMouseEnter={() => setIsNextOnClickActivated(false)}
						onMouseLeave={() => setIsNextOnClickActivated(true)}
						selectedIndex={selectedIndex || 0}
						onSelectIndex={(newIndex) => nextSlide(newIndex)}
					/>
				) : (
					<NGSkeleton width={50} height={10} withBorder />
				)}
			</Flex>
		</StyledSlider>
	);
};

export { Slider };
